/**
 * FontShootout — admin-only comparison page for picking the next "remembr" wordmark font.
 *
 * Six free / web-loadable candidates loaded via Google Fonts + one user-upload slot
 * (for paid commercial fonts like Ngetic Modern that can't be served via CDN until
 * a license + the .ttf/.otf is provided).
 *
 * The picked font is persisted in localStorage; once you and the distributor agree,
 * call the agent to roll the choice into tailwind.config.js + index.css and queue
 * the product/packaging mockup regeneration.
 */
import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle, UploadSimple, Trash, Sparkle } from "@phosphor-icons/react";
import { SiteNav } from "../../components/SiteNav";
import { ForgetMeNotIcon } from "../../components/ForgetMeNotIcon";
import { useAuth } from "../../lib/AuthContext";
import { toast } from "sonner";

const ADMIN_EMAILS = ["lea@millen.com", "glenn@millen.com"];

// Google Fonts → loaded once via <link>. Each candidate carries:
//   family       — actual CSS font-family value
//   stack        — fallback stack
//   designer     — credit (italics)
//   why          — one-liner positioning note
//   googleFont   — CSS @import URL (or null for the upload slot)
//   variantStyle — italic / regular / custom font-variation-settings
//   licence      — note for the user
const CANDIDATES = [
    {
        id: "cormorant",
        label: "Cormorant Garamond",
        family: '"Cormorant Garamond"',
        stack: ", Georgia, serif",
        designer: "Christian Thalmann",
        why: "Current wordmark. Elegant, romantic, soft heritage feel — but increasingly common in SaaS branding.",
        googleFont: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap",
        variantStyle: { fontStyle: "italic", fontWeight: 500 },
        licence: "Free · Google Fonts (SIL OFL) · Current production font",
        accent: "#6B7DB8",
        isCurrent: true,
    },
    {
        id: "fraunces",
        label: "Fraunces",
        family: '"Fraunces"',
        stack: ", Georgia, serif",
        designer: "Phaedra Charles & Flavia Zimbardi (Undercase Type)",
        why: "Modern soft-serif with a romantic italic. Variable axes (SOFT, WONK, OPSZ) — the most flexible candidate. Used by Stripe-adjacent premium SaaS.",
        googleFont: "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght,SOFT,WONK@0,9..144,400..700,0..100,0..1;1,9..144,400..700,0..100,0..1&display=swap",
        variantStyle: { fontStyle: "italic", fontWeight: 500, fontVariationSettings: '"SOFT" 80, "WONK" 0, "opsz" 144' },
        licence: "Free · Google Fonts (SIL OFL) · Commercial use OK",
        accent: "#4A5A95",
    },
    {
        id: "playfair",
        label: "Playfair Display",
        family: '"Playfair Display"',
        stack: ", Georgia, serif",
        designer: "Claus Eggers Sørensen",
        why: "Editorial classic. High contrast, fashion-magazine confidence. Reads premium and very widely understood.",
        googleFont: "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap",
        variantStyle: { fontStyle: "italic", fontWeight: 500 },
        licence: "Free · Google Fonts (SIL OFL) · Commercial use OK",
        accent: "#2F3D6E",
    },
    {
        id: "dm-serif",
        label: "DM Serif Display",
        family: '"DM Serif Display"',
        stack: ", Georgia, serif",
        designer: "Colophon Foundry (commissioned by Google)",
        why: "Bolder, more headline-confident. Reads 'publisher'. Strong character — distinctive without being eccentric.",
        googleFont: "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&display=swap",
        variantStyle: { fontStyle: "italic", fontWeight: 400 },
        licence: "Free · Google Fonts (SIL OFL) · Commercial use OK",
        accent: "#3A4878",
    },
    {
        id: "newsreader",
        label: "Newsreader",
        family: '"Newsreader"',
        stack: ", Georgia, serif",
        designer: "Production Type",
        why: "Warm modern editorial — closest free relative of Recoleta (used by Notion, Calm). Variable axes for fine tuning.",
        googleFont: "https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400..700;1,6..72,400..700&display=swap",
        variantStyle: { fontStyle: "italic", fontWeight: 500, fontVariationSettings: '"opsz" 72' },
        licence: "Free · Google Fonts (SIL OFL) · Commercial use OK",
        accent: "#8B7BA8",
    },
    {
        id: "bodoni",
        label: "Bodoni Moda",
        family: '"Bodoni Moda"',
        stack: ", Georgia, serif",
        designer: "Parmigianino & ATypI volunteers (modern revival)",
        why: "Fashion-magazine high contrast. The most distinctive of the free set — premium luxury, slightly less safe.",
        googleFont: "https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,400..900;1,6..96,400..900&display=swap",
        variantStyle: { fontStyle: "italic", fontWeight: 500, fontVariationSettings: '"opsz" 72' },
        licence: "Free · Google Fonts (SIL OFL) · Commercial use OK",
        accent: "#2D2B2A",
    },
    {
        id: "ngetic-user",
        label: "Ngetic Modern",
        family: "NgeticModernUser",
        stack: ', "Cormorant Garamond", Georgia, serif',
        designer: "Sigit Dwipa (Nirmana Visual)",
        why: "Elegant boutique serif — distributor's recommendation. Commercial font (Creative Market / Envato Elements) — license required before production use.",
        googleFont: null, // upload required
        variantStyle: { fontStyle: "italic", fontWeight: 500 },
        licence: "Commercial — purchase license at creativemarket.com or envato.com, then upload the .ttf/.otf below",
        accent: "#D98E5F",
        needsUpload: true,
    },
];

const PICK_KEY = "remembr:font-shootout-pick";
const UPLOADED_KEY = "remembr:font-shootout-uploaded";

export default function FontShootout() {
    const { user, loading } = useAuth();
    const [picked, setPicked] = useState(() => {
        try { return localStorage.getItem(PICK_KEY) || null; } catch { return null; }
    });
    const [uploadedReady, setUploadedReady] = useState(false);
    const [uploadedFilename, setUploadedFilename] = useState(null);
    const fileInputRef = useRef(null);
    const [size, setSize] = useState(112); // hero font size px
    const [showOnDark, setShowOnDark] = useState(false);

    // Load each candidate's Google Font on mount (idempotent — links de-dupe by href).
    useEffect(() => {
        const created = [];
        for (const c of CANDIDATES) {
            if (!c.googleFont) continue;
            if (document.querySelector(`link[href="${c.googleFont}"]`)) continue;
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = c.googleFont;
            document.head.appendChild(link);
            created.push(link);
        }
        return () => { /* keep loaded — fonts are tiny and cached, cheaper than re-fetching */ };
    }, []);

    // If a previously uploaded font is stashed in localStorage, re-register it.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const stash = localStorage.getItem(UPLOADED_KEY);
                if (!stash) return;
                const { name, dataUrl } = JSON.parse(stash);
                const fontFace = new FontFace("NgeticModernUser", `url(${dataUrl})`);
                await fontFace.load();
                if (cancelled) return;
                document.fonts.add(fontFace);
                setUploadedFilename(name);
                setUploadedReady(true);
            } catch {
                /* corrupted — ignore */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    async function handleUpload(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        const okExt = /\.(ttf|otf|woff2?|TTF|OTF|WOFF2?)$/.test(file.name);
        if (!okExt) {
            toast.error("Please upload a .ttf, .otf, .woff or .woff2 file");
            return;
        }
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const fontFace = new FontFace("NgeticModernUser", `url(${dataUrl})`);
            await fontFace.load();
            document.fonts.add(fontFace);
            try {
                localStorage.setItem(UPLOADED_KEY, JSON.stringify({ name: file.name, dataUrl }));
            } catch {
                toast.warning("Font loaded for this session — too large to persist in localStorage");
            }
            setUploadedFilename(file.name);
            setUploadedReady(true);
            toast.success(`${file.name} loaded into the shootout`);
        } catch (err) {
            toast.error("Could not load that font file: " + (err?.message || "unknown error"));
        }
    }

    function clearUpload() {
        try { localStorage.removeItem(UPLOADED_KEY); } catch { /* ignore */ }
        setUploadedFilename(null);
        setUploadedReady(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast.message("Uploaded font cleared");
    }

    function pick(id) {
        setPicked(id);
        try { localStorage.setItem(PICK_KEY, id); } catch { /* ignore */ }
        const label = CANDIDATES.find((c) => c.id === id)?.label;
        toast.success(`Locked in: ${label}`);
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
                <ForgetMeNotIcon size={48} className="animate-pulse" />
            </div>
        );
    }
    if (!user || user === false) return <Navigate to="/sign-in" replace />;
    if (!ADMIN_EMAILS.includes((user.email || "").toLowerCase())) return <Navigate to="/dashboard" replace />;

    const surface = showOnDark
        ? "bg-[#1a1c25] text-white"
        : "bg-[#FDFBF7] text-[#2D2B2A]";

    return (
        <div className={`min-h-screen ${surface} transition-colors duration-300`}>
            <SiteNav />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24">
                <Link
                    to="/admin"
                    className={`inline-flex items-center gap-2 text-sm mb-6 ${showOnDark ? "text-white/70 hover:text-white" : "text-[#5C5A59] hover:text-[#6B7DB8]"}`}
                    data-testid="shootout-back"
                >
                    <ArrowLeft size={16} /> Back to admin
                </Link>

                <header className="mb-10">
                    <p className={`text-xs uppercase tracking-[0.32em] mb-3 ${showOnDark ? "text-[#A8B5E0]" : "text-[#6B7DB8]"}`}>
                        Brand · Font shootout
                    </p>
                    <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="shootout-title">
                        Pick the next <em className="italic" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>remembr</em>
                    </h1>
                    <p className={`mt-3 max-w-2xl text-sm sm:text-base ${showOnDark ? "text-white/70" : "text-[#5C5A59]"}`}>
                        Seven candidates — six free / web-loadable and one upload slot for the distributor&rsquo;s
                        recommendation (Ngetic Modern). Adjust the size slider, flip dark mode, and click <em>Pick this one</em>
                        when you and the distributor agree. The picked font is saved locally so you can show
                        the choice on this device next visit.
                    </p>
                </header>

                {/* Controls bar */}
                <div className={`mb-8 flex flex-wrap items-center gap-4 p-4 rounded-2xl ${showOnDark ? "bg-white/5 border border-white/10" : "bg-white border border-[#E4E2DD]"}`}>
                    <label className="flex items-center gap-3 text-sm">
                        <span className={showOnDark ? "text-white/70" : "text-[#5C5A59]"}>Hero size</span>
                        <input
                            type="range"
                            min={56}
                            max={200}
                            value={size}
                            onChange={(e) => setSize(Number(e.target.value))}
                            className="w-40 accent-[#4A5A95]"
                            data-testid="shootout-size-slider"
                        />
                        <span className={`tabular-nums text-xs w-10 ${showOnDark ? "text-white/55" : "text-[#5C5A59]"}`}>{size}px</span>
                    </label>
                    <button
                        onClick={() => setShowOnDark((v) => !v)}
                        className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] transition-all ${showOnDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-[#2D2B2A] text-white hover:bg-[#4a4644]"}`}
                        data-testid="shootout-dark-toggle"
                    >
                        {showOnDark ? "Light surface" : "Dark surface"}
                    </button>
                    {picked && (
                        <span className={`ml-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] ${showOnDark ? "text-[#A8B5E0]" : "text-[#4A5A95]"}`}>
                            <CheckCircle size={14} weight="fill" /> Current pick: {CANDIDATES.find((c) => c.id === picked)?.label}
                        </span>
                    )}
                </div>

                {/* Side-by-side strip */}
                <div className={`mb-12 p-6 rounded-2xl ${showOnDark ? "bg-white/5 border border-white/10" : "bg-[#F6F4ED] border border-[#E4E2DD]"}`}>
                    <p className={`text-[10px] uppercase tracking-[0.32em] mb-4 ${showOnDark ? "text-white/55" : "text-[#5C5A59]"}`}>
                        Side-by-side comparison strip
                    </p>
                    <div className="flex flex-wrap items-end gap-x-12 gap-y-6">
                        {CANDIDATES.map((c) => {
                            const available = !c.needsUpload || uploadedReady;
                            return (
                                <div key={c.id} className="text-center">
                                    <span
                                        className="block leading-none"
                                        style={{
                                            fontFamily: c.family + c.stack,
                                            ...c.variantStyle,
                                            fontSize: 56,
                                            color: showOnDark ? "#FDFBF7" : "#2D2B2A",
                                            opacity: available ? 1 : 0.35,
                                        }}
                                    >
                                        {available ? "remembr" : "remembr"}
                                    </span>
                                    <span className={`block text-[10px] uppercase tracking-[0.22em] mt-2 ${showOnDark ? "text-white/55" : "text-[#5C5A59]"}`}>
                                        {c.label}
                                        {c.isCurrent && " · current"}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Candidate cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {CANDIDATES.map((c) => {
                        const isPicked = picked === c.id;
                        const isLocked = c.needsUpload && !uploadedReady;
                        return (
                            <motion.div
                                key={c.id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`relative p-7 rounded-2xl border transition-all ${isPicked
                                    ? (showOnDark ? "bg-white/10 border-[#A8B5E0]" : "bg-white border-[#4A5A95] ring-2 ring-[#4A5A95]/30")
                                    : (showOnDark ? "bg-white/5 border-white/10 hover:bg-white/8" : "bg-white border-[#E4E2DD] hover:border-[#A8B5E0]")
                                }`}
                                data-testid={`candidate-${c.id}`}
                            >
                                <div className="flex items-start justify-between gap-4 mb-6">
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.28em] mb-1" style={{ color: c.accent }}>
                                            {c.isCurrent ? "Current" : "Candidate"}
                                        </p>
                                        <h3 className={`font-serif text-2xl ${showOnDark ? "text-white" : "text-[#2D2B2A]"}`}>{c.label}</h3>
                                        <p className={`text-xs italic mt-1 ${showOnDark ? "text-white/55" : "text-[#5C5A59]"}`}>{c.designer}</p>
                                    </div>
                                    {isPicked ? (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#4A5A95] text-white text-xs uppercase tracking-[0.18em]" data-testid={`picked-${c.id}`}>
                                            <CheckCircle size={14} weight="fill" /> Picked
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => pick(c.id)}
                                            disabled={isLocked}
                                            className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.18em] transition-all hover:-translate-y-0.5 ${isLocked
                                                ? (showOnDark ? "bg-white/5 text-white/30 cursor-not-allowed" : "bg-[#F6F4ED] text-[#B5B3AD] cursor-not-allowed")
                                                : (showOnDark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-[#2D2B2A] hover:bg-[#4a4644] text-white")
                                            }`}
                                            data-testid={`pick-${c.id}`}
                                            aria-label={`Pick ${c.label}`}
                                        >
                                            <Sparkle size={12} weight="fill" className="inline mr-1.5" />
                                            Pick this one
                                        </button>
                                    )}
                                </div>

                                {/* Hero wordmark */}
                                <div className={`flex items-center justify-center py-8 px-4 rounded-xl mb-5 ${showOnDark ? "bg-[#0F1116]" : "bg-[#FDFBF7]"}`}>
                                    {isLocked ? (
                                        <span className="text-sm italic text-center" style={{ color: showOnDark ? "rgba(255,255,255,0.4)" : "#B5B3AD" }}>
                                            Upload Ngetic Modern below to preview it here
                                        </span>
                                    ) : (
                                        <span
                                            className="leading-none"
                                            style={{
                                                fontFamily: c.family + c.stack,
                                                ...c.variantStyle,
                                                fontSize: size,
                                                color: showOnDark ? "#FDFBF7" : "#2D2B2A",
                                            }}
                                            data-testid={`hero-${c.id}`}
                                        >
                                            remembr
                                        </span>
                                    )}
                                </div>

                                {/* BrandLockup-style preview */}
                                <div className="grid grid-cols-3 gap-4 mb-5">
                                    {/* SiteNav size with FMN icon */}
                                    <div className={`flex flex-col items-center justify-center py-4 px-2 rounded-xl ${showOnDark ? "bg-[#0F1116]" : "bg-[#FDFBF7]"}`}>
                                        <ForgetMeNotIcon size={32} variant={showOnDark ? "mono" : "colour"} />
                                        <span
                                            className="mt-1"
                                            style={{
                                                fontFamily: c.family + c.stack,
                                                ...c.variantStyle,
                                                fontSize: 22,
                                                color: showOnDark ? "#FDFBF7" : "#2D2B2A",
                                                opacity: isLocked ? 0.35 : 1,
                                            }}
                                        >
                                            remembr
                                        </span>
                                        <span className={`block text-[8px] uppercase tracking-[0.22em] mt-1 ${showOnDark ? "text-white/40" : "text-[#8A8786]"}`}>
                                            SiteNav size
                                        </span>
                                    </div>
                                    {/* Tablet bezel sim */}
                                    <div className={`flex flex-col items-center justify-center py-4 px-2 rounded-xl ${showOnDark ? "bg-[#0F1116]" : "bg-[#FDFBF7]"}`}>
                                        <span
                                            style={{
                                                fontFamily: c.family + c.stack,
                                                ...c.variantStyle,
                                                fontSize: 14,
                                                letterSpacing: 2,
                                                color: showOnDark ? "#FDFBF7" : "#5C5A59",
                                                opacity: isLocked ? 0.35 : 1,
                                                textTransform: c.id === "dm-serif" || c.id === "bodoni" ? "uppercase" : "none",
                                            }}
                                        >
                                            remembr
                                        </span>
                                        <span className={`block text-[8px] uppercase tracking-[0.22em] mt-2 ${showOnDark ? "text-white/40" : "text-[#8A8786]"}`}>
                                            Tablet bezel sim
                                        </span>
                                    </div>
                                    {/* Box-front lockup */}
                                    <div className={`flex flex-col items-center justify-center py-4 px-2 rounded-xl ${showOnDark ? "bg-[#0F1116]" : "bg-[#FDFBF7]"}`}>
                                        <span
                                            style={{
                                                fontFamily: c.family + c.stack,
                                                ...c.variantStyle,
                                                fontSize: 28,
                                                color: showOnDark ? "#FDFBF7" : "#2D2B2A",
                                                opacity: isLocked ? 0.35 : 1,
                                            }}
                                        >
                                            remembr
                                        </span>
                                        <span
                                            className="mt-0.5"
                                            style={{
                                                fontFamily: c.family + c.stack,
                                                fontStyle: "italic",
                                                fontSize: 9,
                                                letterSpacing: 2,
                                                color: showOnDark ? "rgba(255,255,255,0.55)" : "#8A8786",
                                                opacity: isLocked ? 0.35 : 1,
                                            }}
                                        >
                                            forget me not.
                                        </span>
                                        <span className={`block text-[8px] uppercase tracking-[0.22em] mt-2 ${showOnDark ? "text-white/40" : "text-[#8A8786]"}`}>
                                            Box-front lockup
                                        </span>
                                    </div>
                                </div>

                                {/* Tagline */}
                                <div className={`text-center py-3 px-4 rounded-xl mb-5 ${showOnDark ? "bg-[#0F1116]" : "bg-[#FDFBF7]"}`}>
                                    <p
                                        style={{
                                            fontFamily: c.family + c.stack,
                                            fontStyle: "italic",
                                            fontWeight: 400,
                                            fontSize: 18,
                                            color: showOnDark ? "rgba(255,255,255,0.8)" : "#4A5A95",
                                            opacity: isLocked ? 0.35 : 1,
                                        }}
                                    >
                                        Yesterday &middot; Today &middot; Tomorrow &mdash; forget me not.
                                    </p>
                                </div>

                                {/* Why + licence */}
                                <p className={`text-sm leading-relaxed mb-2 ${showOnDark ? "text-white/75" : "text-[#5C5A59]"}`}>
                                    {c.why}
                                </p>
                                <p className={`text-[10px] uppercase tracking-[0.22em] ${showOnDark ? "text-white/40" : "text-[#8A8786]"}`}>
                                    {c.licence}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Upload slot for Ngetic Modern */}
                <div className={`mt-10 p-7 rounded-2xl border ${showOnDark ? "bg-white/5 border-white/10" : "bg-white border-[#E4E2DD]"}`}>
                    <h3 className={`font-serif text-xl mb-2 ${showOnDark ? "text-white" : "text-[#2D2B2A]"}`}>
                        Upload the distributor&rsquo;s font
                    </h3>
                    <p className={`text-sm mb-4 ${showOnDark ? "text-white/70" : "text-[#5C5A59]"}`}>
                        Once you&rsquo;ve licensed <em>Ngetic Modern</em> (or any other commercial font the
                        distributor recommends), drop the <code>.ttf</code> / <code>.otf</code> / <code>.woff(2)</code>
                        file here. It loads instantly into the Ngetic Modern card above and into the comparison strip
                        &mdash; persisted in your browser for next visit, never uploaded to the server.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                        <label
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D98E5F] hover:bg-[#C97B4A] text-white text-sm font-medium cursor-pointer transition-all"
                            data-testid="shootout-upload-label"
                        >
                            <UploadSimple size={16} weight="bold" />
                            {uploadedReady ? "Replace font" : "Upload font file"}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".ttf,.otf,.woff,.woff2,application/octet-stream,font/*"
                                onChange={handleUpload}
                                className="hidden"
                                data-testid="shootout-upload-input"
                            />
                        </label>
                        {uploadedReady && (
                            <>
                                <span className={`text-sm ${showOnDark ? "text-white/75" : "text-[#5C5A59]"}`} data-testid="shootout-upload-filename">
                                    <CheckCircle size={14} weight="fill" className="inline mr-1.5 text-[#4A5A95]" />
                                    {uploadedFilename}
                                </span>
                                <button
                                    onClick={clearUpload}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] transition-all ${showOnDark ? "bg-white/10 hover:bg-white/20 text-white/80" : "bg-[#F6F4ED] hover:bg-[#E4E2DD] text-[#5C5A59]"}`}
                                    data-testid="shootout-upload-clear"
                                >
                                    <Trash size={12} weight="bold" /> Clear
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* What happens next */}
                <div className={`mt-10 p-6 rounded-2xl border-l-4 ${showOnDark ? "bg-white/5 border-l-[#A8B5E0] text-white/80" : "bg-[#F6F4ED] border-l-[#4A5A95] text-[#5C5A59]"}`} data-testid="shootout-next-steps">
                    <p className="text-xs uppercase tracking-[0.24em] mb-2" style={{ color: showOnDark ? "#A8B5E0" : "#4A5A95" }}>
                        Once you&rsquo;ve picked
                    </p>
                    <p className="text-sm leading-relaxed">
                        Reply with the winner&rsquo;s name and I&rsquo;ll roll the swap across <code>tailwind.config.js</code>,
                        <code>index.css</code>, <code>TabletHeadphonesIllustration.jsx</code>, the <code>BrandLockup</code>,
                        and the ReportLab PDF stylesheet &mdash; then queue Nano Banana regenerations of the 21 product /
                        packaging / hardware mockups so the site and the physical product photography stay in lockstep.
                        No inconsistency window.
                    </p>
                </div>
            </div>
        </div>
    );
}
